import { ReactElement, useEffect, useState, KeyboardEvent } from "react";
import { uid } from "react-uid";
import { classJoin } from "@ps/utils";
import {
  FilterDropdown,
  SimpleFilterOption,
  TableLabel,
} from "@ps/ui-components";
import { useTranslationWithNamespace } from "../../../hooks";
import {
  FOLDER_NAME,
  LAST_UPDATE,
  FOLDER_URL,
  FOLDERS_PIN,
  RESUMES,
  ANONYMIZED,
  CLIENT,
  CLIENT_NAME,
  STATUS,
} from "../../constants";
import { SortBarProps } from "./types";
import styles from "./styles.module.css";
import { FilterItem } from "../types";
import {
  getCheckedNames,
  selectFilterOptions,
} from "../../../prospect-domain/utils";
import { FOLDERS_LIST } from "../../../shared/data-cy";
import Keys from "../../../shared/keys";
import { prepareClientFilterOptions } from "../../utils/filterFoldersList";

const DROPDOWN_ID = "filter-dropdown";

const SortBar = ({
  clientId,
  folders,
  filters,
  updateFilters,
}: SortBarProps): ReactElement => {
  const { t } = useTranslationWithNamespace();
  const translate = (field: string) => t(`folders.list.${field}`);
  const [searchValue, setSearchValue] = useState("");
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [currentIDFocused, setCurrentIDFocused] = useState("");

  useEffect(() => {
    if (showFilterOptions) document.getElementById(DROPDOWN_ID)?.focus();
  }, [showFilterOptions]);

  const clientFiltersOptions: FilterItem[] =
    prepareClientFilterOptions(folders) || [];

  const renderLabel = (
    fieldName: string,
    translationName?: string,
    filterOptions: undefined | FilterItem[] = undefined,
    filterName?: string | undefined,
  ): ReactElement => (
    <TableLabel
      dataCy={translationName || fieldName}
      setSearchValue={setSearchValue}
      filtering={
        filterOptions
          ? {
              checked: getCheckedNames(filters, filterName || fieldName),
              dropdownOverlay: (
                <FilterDropdown
                  dataCy={`${FOLDERS_LIST}-${filterName}`}
                  DROPDOWN_ID={DROPDOWN_ID}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  setShowFilterOptions={setShowFilterOptions}
                  filterOptions={filterOptions}
                >
                  {filterOptions.map(
                    (item: { id: string; name: string }, index: number) => {
                      const isChecked = filters[filterName || fieldName]?.some(
                        ({ id }: FilterItem) => id === item.id,
                      );
                      const autoFocusId = `${filterName}-${index}`;

                      return (
                        <SimpleFilterOption
                          key={uid(item)}
                          autoFocus={autoFocusId === currentIDFocused}
                          item={item}
                          isChecked={isChecked}
                          onKeyDown={(
                            event: KeyboardEvent<HTMLButtonElement>,
                          ): void => {
                            if (event.code === Keys.SPACE) {
                              event.preventDefault();
                              updateFilters(
                                isChecked,
                                item,
                                filterName || fieldName,
                              );
                              setCurrentIDFocused(autoFocusId);
                            }
                          }}
                          onClick={() => {
                            updateFilters(
                              isChecked,
                              item,
                              filterName || fieldName,
                            );
                            setCurrentIDFocused(autoFocusId);
                          }}
                        />
                      );
                    },
                  )}
                </FilterDropdown>
              ),
            }
          : undefined
      }
    >
      {translate(translationName || fieldName)}
    </TableLabel>
  );

  return (
    <div
      className={classJoin(
        styles.gridContainer,
        "2xl:gap-4 gap-2 bg-neutralSecondary-41 bg-opacity-10 rounded-t-2xl h-16 items-center",
        clientId ? "" : "mt-8",
      )}
    >
      <TableLabel dataCy={FOLDER_NAME}>{translate(FOLDER_NAME)}</TableLabel>
      {renderLabel(
        CLIENT_NAME,
        CLIENT,
        selectFilterOptions(clientFiltersOptions, searchValue),
        CLIENT_NAME,
      )}
      <TableLabel dataCy={FOLDER_URL}>{translate(FOLDER_URL)}</TableLabel>
      <TableLabel dataCy={FOLDERS_PIN}>{translate(FOLDERS_PIN)}</TableLabel>
      <TableLabel dataCy={ANONYMIZED}>{translate(ANONYMIZED)}</TableLabel>
      <TableLabel dataCy={LAST_UPDATE}>{translate(LAST_UPDATE)}</TableLabel>
      <TableLabel dataCy={RESUMES}>{translate(RESUMES)}</TableLabel>
      <TableLabel dataCy={STATUS}>{translate(STATUS)}</TableLabel>

      <span className="invisible" />
    </div>
  );
};

export default SortBar;
